import React from 'react';
import { constants } from '@yola/subscription-manager-js';
import { designSystem } from '@yola/ws-ui';
import actionIds from '../constants/action-ids';
import { t } from '../../localization';
import configStorage from '../../configuration/config-storage';
import {
  getRegistrationPricePerYear,
  getDomainPricePerYear,
  getPricePerMonth,
  getPricePerYear,
} from '../../common/helpers/get-price-data';
import getDomainParts from '../../common/helpers/get-domains-parts';
import yoproductsServiceClient from '../../common/helpers/yoproducts-service-client';

const { TextSkeleton, Paragraph, Mark } = designSystem;
const { MONTHLY, ANNUAL } = constants.SubscriptionTerm;

const getPlanItems = ({
  plan,
  prices,
  currency,
  domainName,
  isLoading,
  selectedTerm,
  actionId,
}) => {
  const { locale = 'en' } = configStorage.get();
  const yoproducts = yoproductsServiceClient.get();
  const pricePerMonth = getPricePerMonth({ prices, plan: plan.type, currency });
  const pricePerYearTotal = getPricePerYear({ prices, plan: plan.type, currency });
  const pricePerYearMonth = (pricePerYearTotal / 12).toFixed(2);
  const planDiscount = pricePerMonth * 12 - pricePerYearTotal;

  let domainDiscount = 0;
  let domainPrice = 0;
  let registrationPrice = 0;

  if (actionId === actionIds.BUY_DOMAIN) {
    const { tldParts } = getDomainParts(domainName);

    const params = { prices, tld: tldParts.join('.'), currency };
    domainPrice = getDomainPricePerYear(params);
    const discountedDomainPrice = getDomainPricePerYear(params, 'discounted');
    domainDiscount = domainPrice - discountedDomainPrice;

    registrationPrice = parseFloat(getRegistrationPricePerYear({ prices, currency }), 10);
  }

  const discountAmount = (planDiscount + domainDiscount + registrationPrice).toFixed(2);

  const formattedPricePerMonth = yoproducts.actions.formatPrice(pricePerMonth, locale, currency, {
    leadingCurrency: true,
  });
  const formattedPricePerYearMonth = yoproducts.actions.formatPrice(
    pricePerYearMonth,
    locale,
    currency,
    { leadingCurrency: true }
  );
  const formattedDomainPrice = yoproducts.actions.formatPrice(domainPrice, locale, currency, {
    leadingCurrency: true,
  });
  const formattedDiscountAmount = yoproducts.actions.formatPrice(discountAmount, locale, currency, {
    leadingCurrency: true,
  });

  const priceSkeleton = (
    <span className="ws-automatic-plan-selector-step-line__item-price-skeleton">
      <TextSkeleton width="80px" />
    </span>
  );

  const discountAmountSkeleton = `<span class="ws-automatic-plan-selector-step-line__item-amount-price-skeleton">
      <span class="ws-ui-text-skeleton" style="max-width:40px"><span class="ws-ui-text-skeleton__line-container">&nbsp;<span class="ws-ui-text-skeleton__line"></span></span></span>
    </span>`;

  let annualDescription;
  let monthlyDescription;

  switch (actionId) {
    case actionIds.BUY_DOMAIN: {
      annualDescription = t(
        'Free 14-day trial. Save {discountAmount} on your domain and website plan.',
        {
          discountAmount: isLoading ? discountAmountSkeleton : formattedDiscountAmount,
        }
      );
      monthlyDescription = t('Free 14-day trial. No domain discount.');
      break;
    }
    case actionIds.THIRD_PARTY_DOMAIN:
    case actionIds.GET_FREE_DOMAIN:
    default:
      annualDescription = t('Free 14-day trial. Save {discountAmount} on your website plan.', {
        discountAmount: isLoading ? discountAmountSkeleton : formattedDiscountAmount,
      });
      monthlyDescription = t('Free 14-day trial. Cancel anytime.');
      break;
  }

  return [
    {
      id: ANNUAL,
      title: t('Annual {planName} plan', { planName: plan.name }),
      label: t('Best value'),
      description: (
        <Paragraph weight="medium" appearance="success">
          <span
            // eslint-disable-next-line yola/react/no-danger
            dangerouslySetInnerHTML={{
              __html: annualDescription,
            }}
          />
        </Paragraph>
      ),
      summaryLabel: isLoading
        ? priceSkeleton
        : t('{price}/mo', { price: formattedPricePerYearMonth }),
      summaryNote: t('billed annually'),
      withDecoration: true,
    },
    {
      id: MONTHLY,
      title: t('Monthly {planName} plan', { planName: plan.name }),
      label: '',
      description: (
        <Paragraph>
          {monthlyDescription}
          {actionId === actionIds.BUY_DOMAIN && selectedTerm === MONTHLY && (
            <Mark appearance="danger" weight="medium">
              {t('Domain discount removed. You’ll now pay {price}/year for your domain', {
                price: formattedDomainPrice,
              })}
            </Mark>
          )}
        </Paragraph>
      ),
      summaryLabel: isLoading ? priceSkeleton : t('{price}/mo', { price: formattedPricePerMonth }),
    },
  ];
};

export default getPlanItems;
